// Cari Paket: a small dialog with one search field over the user's own
// projects of both sides (GET /search/projects?q=), opened from the icon
// button at the top right of the main area (shell/ChatShell.tsx). Typing is
// debounced; under 2 characters nothing is searched. The arrow keys move
// through the results, Enter opens the active one, Esc closes. A result is a
// link to the project (its href), so a click opens it and closes the dialog.
import { useEffect, useId, useState, type KeyboardEvent } from "react";
import * as API from "../api";
import { formatRelative } from "../lib/format";
import { navigate } from "../router";
import { Dialog, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";

/** The API answers nothing (tooShort) under this many characters. */
export const MIN_QUERY = 2;
const LIMIT = 20;
const DEBOUNCE_MS = 250;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const KIND_LABEL: Record<API.ProjectSearchResult["kind"], string> = {
  paket: "Paket Penyedia",
  pbj: "Paket PBJ",
  folder: "Project",
};

/** The line under a result's name: kind, instansi and stage, kode, last change. */
export function resultMeta(r: API.ProjectSearchResult): string {
  const parts = [KIND_LABEL[r.kind] ?? "Project", r.subtitle, r.kode ? `Kode ${r.kode}` : null, formatRelative(r.updatedAt)];
  return parts.filter((p): p is string => !!p && p.trim().length > 0).join(" · ");
}

export function SearchProjectsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const base = useId();
  const listId = `${base}-list`;
  const typed = q.replace(/\s+/g, " ").trim();
  const query = useDebounced(typed, DEBOUNCE_MS);
  const enabled = open && query.length >= MIN_QUERY;
  const search = useAsync(() => (enabled ? API.searchProjects(query, LIMIT) : Promise.resolve(null)), [enabled, query]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);
  useEffect(() => setActive(0), [query]);

  const short = typed.length < MIN_QUERY || search.data?.tooShort === true;
  const pending = !short && (typed !== query || search.loading);
  const rows = !short && search.data && search.data.query === query ? search.data.results : [];
  const current = rows.length > 0 ? Math.min(active, rows.length - 1) : -1;
  const optionId = (i: number) => `${base}-opt-${i}`;

  const go = (r: API.ProjectSearchResult) => {
    onClose();
    navigate(r.href);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (rows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((current + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((current - 1 + rows.length) % rows.length);
    } else if (e.key === "Home" && e.ctrlKey) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End" && e.ctrlKey) {
      e.preventDefault();
      setActive(rows.length - 1);
    } else if (e.key === "Enter" && current >= 0) {
      e.preventDefault();
      go(rows[current]!);
    }
  };

  // Keep the active row in view while the arrows move through a long list.
  useEffect(() => {
    if (current < 0) return;
    document.getElementById(optionId(current))?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  let body;
  if (short) {
    body = <p className="cs-search-note">Ketik minimal {MIN_QUERY} huruf nama paket, instansi, atau kode.</p>;
  } else if (search.error && !pending) {
    body = (
      <p className="cs-search-note" role="alert">
        {API.errorMessage(search.error)}
      </p>
    );
  } else if (pending && rows.length === 0) {
    body = (
      <p className="cs-search-note" role="status">
        Mencari paket
      </p>
    );
  } else if (rows.length === 0) {
    body = <p className="cs-search-note">Tidak ada paket yang cocok dengan pencarian ini.</p>;
  } else {
    body = (
      <ul id={listId} className="cs-search-list" role="listbox" aria-label="Hasil pencarian paket" aria-busy={pending || undefined}>
        {rows.map((r, i) => (
          <li key={r.id} role="none">
            <a
              id={optionId(i)}
              className="cs-search-row"
              role="option"
              aria-selected={i === current}
              tabIndex={-1}
              href={r.href}
              onClick={onClose}
              onMouseMove={() => i !== current && setActive(i)}
            >
              <span className="cs-search-name">{r.name}</span>
              <span className="cs-search-meta">{resultMeta(r)}</span>
            </a>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Dialog open={open} title="Cari Paket" size="sm" onClose={onClose}>
      <div className="cs-search">
        <div className="control-wrap search-wrap">
          <Glyph name="searchNormal" />
          <input
            id="cs-search-q"
            type="search"
            className="control-icon-start"
            value={q}
            placeholder="Nama paket, instansi, atau kode"
            aria-label="Cari Paket"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={rows.length > 0}
            aria-controls={listId}
            aria-activedescendant={current >= 0 ? optionId(current) : undefined}
            autoComplete="off"
            data-autofocus=""
            onChange={(e) => setQ(e.currentTarget.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        {open ? body : null}
      </div>
    </Dialog>
  );
}
